import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { compact, money, pct } from '@/utils/format';
import {
  Brain, Sparkles, AlertTriangle, Download, Database, ArrowUpRight,
  Activity, Bot, Send, X, Cpu, Zap, RefreshCw, TrendingUp, Wallet, Wrench,
  Home as HomeIcon, Users, Building2, FileText, Radio, ChevronRight, Loader2, ArrowRight, Check,
} from 'lucide-react';
import Layout from '@/components/Layout';
import ExecutiveIntelligenceHub from '@/components/ExecutiveIntelligenceHub';

// ─────────────────────────────────────────────────────────────────────────────
// SVG primitives — built locally to match the cinematic command-center vibe.
// No external chart libraries needed; everything emerald-themed.
// ─────────────────────────────────────────────────────────────────────────────

function RingGauge({ value = 0, label, sub, size = 116, stroke = 8, tone = 'signal' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  const off = c - (v / 100) * c;
  const colors = {
    signal: ['#1FD18A', '#00A86B'],
    amber:  ['#FFB547', '#D78A1F'],
    coral:  ['#FF8A75', '#E5523B'],
  };
  const [a, b] = colors[tone] || colors.signal;
  const gid = `rg-${label}-${tone}`;
  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <defs>
            <linearGradient id={gid} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor={a} />
              <stop offset="100%" stopColor={b} />
            </linearGradient>
          </defs>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EDF1F4" strokeWidth={stroke} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`url(#${gid})`} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.2,0.8,0.2,1)' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[26px] reva-mono text-[#0E1320] tabular-nums leading-none">{v.toFixed(0)}%</div>
          {sub && <div className="text-[10px] reva-mono text-[#71777E] mt-1 uppercase tracking-[0.14em]">{sub}</div>}
        </div>
      </div>
      <div className="mt-3 text-[11px] reva-mono uppercase tracking-[0.16em] text-[#2A3340]">{label}</div>
    </div>
  );
}

function AreaChart({ points = [], height = 180, label = '', metric = '', delta = '' }) {
  const W = 720, H = height, P = 14;
  if (!points.length) {
    return (
      <div className="h-[180px] flex items-center justify-center text-[12px] reva-mono text-[#9CA3AF]">
        <span className="inline-flex items-center gap-2"><Database className="h-3.5 w-3.5" /> no data</span>
      </div>
    );
  }
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = Math.max(max - min, 1);
  const x = (i) => P + (i * (W - 2 * P)) / Math.max(points.length - 1, 1);
  const y = (v) => H - P - ((v - min) * (H - 2 * P)) / span;
  const path = points.map((v, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(v)}`).join(' ');
  const area = path + ` L ${x(points.length - 1)} ${H - P} L ${x(0)} ${H - P} Z`;
  const last = points.length - 1;

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">{label}</div>
        <div className="flex items-center gap-3">
          {metric && <div className="text-[16px] reva-mono text-[#0E1320] tabular-nums">{metric}</div>}
          {delta && <div className="text-[10px] reva-mono text-[#00734A]">{delta}</div>}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
        <defs>
          <linearGradient id="rg-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1FD18A" stopOpacity="0.40" />
            <stop offset="100%" stopColor="#1FD18A" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {Array.from({ length: 4 }).map((_, i) => (
          <line key={i} x1={P} x2={W - P} y1={P + i * ((H - 2 * P) / 3)} y2={P + i * ((H - 2 * P) / 3)} stroke="#ECEFF3" />
        ))}
        <path d={area} fill="url(#rg-area)" />
        <path d={path} fill="none" stroke="#1FD18A" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(last)} cy={y(points[last])} r="4" fill="#1FD18A" />
        <circle cx={x(last)} cy={y(points[last])} r="8" fill="#1FD18A" opacity="0.22" className="reva-pulse" />
      </svg>
    </div>
  );
}

function BarSpark({ values = [], labels = [], height = 144 }) {
  const W = 360, H = height, P = 10;
  const max = Math.max(...values, 1);
  const bw = Math.max(6, (W - 2 * P) / Math.max(values.length, 1) - 4);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <defs>
        <linearGradient id="rg-bar" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1FD18A" stopOpacity="1" />
          <stop offset="100%" stopColor="#00734A" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      {values.map((v, i) => {
        const h = (v / max) * (H - 2 * P - 14);
        const x = P + i * ((W - 2 * P) / values.length);
        return (
          <g key={i}>
            <rect x={x} y={H - P - h - 8} width={bw} height={h} rx="2" fill="url(#rg-bar)" opacity="0.85" />
            <rect x={x} y={H - P - 8} width={bw} height="2" rx="1" fill="rgba(0,168,107,0.5)" />
            {labels[i] && <text x={x + bw / 2} y={H - 1} fontSize="9" textAnchor="middle" fill="#71777E" fontFamily="JetBrains Mono">{labels[i]}</text>}
          </g>
        );
      })}
    </svg>
  );
}

// Saudi portfolio map — simulated emerald city network
const SAUDI_CITIES = [
  { id: 'ryd', name_en: 'Riyadh',   name_ar: 'الرياض',   x: 290, y: 165 },
  { id: 'jed', name_en: 'Jeddah',   name_ar: 'جدة',     x: 132, y: 230 },
  { id: 'dmm', name_en: 'Dammam',   name_ar: 'الدمام',  x: 380, y: 145 },
  { id: 'mka', name_en: 'Mecca',    name_ar: 'مكة',     x: 152, y: 240 },
  { id: 'med', name_en: 'Madinah',  name_ar: 'المدينة', x: 175, y: 175 },
  { id: 'abh', name_en: 'Abha',     name_ar: 'أبها',     x: 215, y: 305 },
  { id: 'tbk', name_en: 'Tabuk',    name_ar: 'تبوك',    x: 145, y: 100 },
  { id: 'hfr', name_en: 'Hofuf',    name_ar: 'الهفوف',  x: 360, y: 195 },
];

function PortfolioMap({ properties = [], isAr }) {
  // Aggregate property counts per city by simple match on property.city
  const counts = useMemo(() => {
    const c = {};
    properties.forEach(p => {
      const key = (p.city || '').toLowerCase();
      const m = SAUDI_CITIES.find(s => key.includes(s.name_en.toLowerCase()) || key.includes(s.name_ar)) || SAUDI_CITIES[0];
      c[m.id] = (c[m.id] || 0) + 1;
    });
    return c;
  }, [properties]);

  const W = 500, H = 360;
  const center = SAUDI_CITIES[0]; // Riyadh is the operational hub

  return (
    <div className="relative h-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="mc-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1FD18A" stopOpacity="0.5" />
            <stop offset="60%" stopColor="#00A86B" stopOpacity="0.10" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>

        {/* Outline of KSA — simplified abstract */}
        <path
          d="M 120 80 Q 100 130 105 175 Q 110 220 140 260 Q 175 305 230 320 Q 290 335 340 320 Q 400 305 425 270 Q 445 240 430 200 Q 420 165 405 130 Q 380 95 320 80 Q 245 65 175 70 Q 140 73 120 80 Z"
          fill="rgba(0,168,107,0.04)"
          stroke="rgba(0,168,107,0.30)"
          strokeWidth="1"
          strokeDasharray="3 5"
        />

        {/* Connections from Riyadh */}
        {SAUDI_CITIES.slice(1).map((c, i) => {
          const dx = c.x - center.x;
          const dy = c.y - center.y;
          const cx = center.x + dx * 0.45 + (i % 2 === 0 ? -18 : 18);
          const cy = center.y + dy * 0.45 - 28;
          return (
            <g key={c.id}>
              <path d={`M ${center.x} ${center.y} Q ${cx} ${cy} ${c.x} ${c.y}`}
                fill="none" stroke="rgba(0,168,107,0.45)" strokeWidth="1" strokeDasharray="2 4" />
            </g>
          );
        })}

        {/* City nodes */}
        {SAUDI_CITIES.map((c) => {
          const count = counts[c.id] || 0;
          const isHub = c.id === 'ryd';
          const r = isHub ? 6 : (count > 0 ? 4.5 : 3);
          return (
            <g key={c.id}>
              {isHub && <circle cx={c.x} cy={c.y} r="32" fill="url(#mc-glow)" />}
              <circle cx={c.x} cy={c.y} r={r + 6} fill="#1FD18A" opacity="0.18" className="reva-pulse" style={{ animationDelay: `${SAUDI_CITIES.indexOf(c) * 0.4}s` }} />
              <circle cx={c.x} cy={c.y} r={r} fill={isHub ? '#1FD18A' : (count > 0 ? '#00A86B' : 'rgba(255,255,255,0.55)')} />
              {(isHub || count > 0) && (
                <text x={c.x + 8} y={c.y + 3} fontSize="9.5" fill="#F2F4F6" fontFamily="JetBrains Mono" opacity="0.85">
                  {isAr ? c.name_ar : c.name_en}{count > 0 ? ` · ${count}` : ''}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {/* Legend pill */}
      <div className="absolute top-2 end-2 flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border border-[#E1E6EC] text-[10px] reva-mono text-[#4A5263]">
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--reva-signal-2)] reva-pulse" />
        {isAr ? 'شبكة المحفظة · حية' : 'PORTFOLIO MESH · LIVE'}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Assistant panel — slide-in cockpit chat
// ─────────────────────────────────────────────────────────────────────────────
function AIAssistant({ open, onClose, isAr, stats, vacancies, overdue }) {
  const [msgs, setMsgs] = useState(() => ([
    { from: 'ai', t: isAr ? 'صباح الخير. أنا REVA — مساعد ذكاء عملياتك. اسأل عن أي شيء يخص محفظتك.' : 'Good morning. I am REVA — your operational intelligence copilot. Ask about anything in your portfolio.' },
  ]));
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  if (!open) return null;
  const sendDraft = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || sending) return;
    setMsgs(m => [...m, { from: 'me', t: text }]);
    setDraft('');
    setSending(true);
    try {
      const history = [...msgs, { from: 'me', t: text }].map(m => ({ role: m.from === 'me' ? 'user' : 'assistant', content: m.t }));
      const res = await api.aiAssistantChat(history, isAr ? 'ar' : 'en', sessionId);
      setSessionId(res.data.session_id || sessionId);
      setMsgs(m => [...m, { from: 'ai', t: res.data.reply || (isAr ? 'تعذّر الحصول على رد الآن.' : 'No reply.') }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsgs(m => [...m, { from: 'ai', t: isAr ? 'تعذّر الاتصال بمساعد REVA. حاول مرة أخرى بعد قليل.' : (detail || 'Could not reach the REVA assistant. Try again shortly.') }]);
    } finally { setSending(false); }
  };

  // Render via portal so the aside escapes Layout's z-[2] stacking context
  // (otherwise the sticky header (z-20) would intercept clicks on the close button).
  return createPortal(
    <aside className="fixed inset-y-0 end-0 w-full sm:w-[360px] md:w-[400px] z-[80] bg-white border-l border-[#E6EAF0] flex flex-col shadow-2xl" style={{ borderRadius: 0 }} dir={isAr ? 'rtl' : 'ltr'} data-testid="ai-assistant-panel">
      <div className="px-5 py-4 border-b border-[#E6EAF0] flex items-center gap-3">
        <div className="relative w-9 h-9 rounded-full flex items-center justify-center bg-[color:var(--reva-signal)]/12 border border-[color:var(--reva-signal)]/35">
          <Brain className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.8} />
          <span className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 rounded-full bg-[color:var(--reva-mint)] border-2 border-white reva-pulse" />
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-[#0E1320]">REVA · AI Assistant</div>
          <div className="text-[10.5px] reva-mono text-[#00734A] flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[color:var(--reva-mint)]" />
            {isAr ? 'متّصل · يستمع' : 'connected · listening'}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-[#F1F3F6] rounded-md text-[#4A5263]" data-testid="ai-assistant-close" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFBFC]">
        {msgs.map((m, i) => (
          m.from === 'ai' ? (
            <div key={i} className="flex items-end gap-2">
              <div className="w-7 h-7 shrink-0 rounded-full bg-[color:var(--reva-signal)]/15 border border-[color:var(--reva-signal)]/30 flex items-center justify-center">
                <Sparkles className="h-3 w-3 text-[color:var(--reva-signal)]" strokeWidth={1.8} />
              </div>
              <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-[#F1F3F6] border border-[#E6EAF0] px-3.5 py-2.5 text-[13px] text-[#0E1320] leading-relaxed">
                {m.t}
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-end justify-end gap-2">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[color:var(--reva-signal)] text-white px-3.5 py-2.5 text-[13px] leading-relaxed">
                {m.t}
              </div>
            </div>
          )
        ))}

        {/* Suggested prompts */}
        <div className="pt-2">
          <div className="text-[10px] reva-mono tracking-[0.16em] uppercase text-[#9CA3AF] mb-2">{isAr ? 'مهام مقترحة' : 'Suggested'}</div>
          <div className="flex flex-wrap gap-2">
            {[
              isAr ? 'لخّص أداء الربع' : 'Summarize this quarter',
              isAr ? 'اعرض المخاطر' : 'Show portfolio risks',
              isAr ? `${money(overdue || 0, { lang: 'ar' })} متأخرات` : `${money(overdue || 0)} overdue`,
            ].map((q, i) => (
              <button key={i} onClick={() => sendDraft(q)} disabled={sending} className="text-[11.5px] px-2.5 py-1 rounded-full border border-[#E1E6EC] bg-[#F7F8FA] hover:border-[color:var(--reva-signal)]/40 hover:bg-[color:var(--reva-signal)]/[0.06] text-[#1A1F2A] transition-colors disabled:opacity-50" data-testid={`ai-suggest-${i}`}>
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-[#E6EAF0]">
        <div className="flex items-center gap-2 rounded-full bg-[#F7F8FA] border border-[#E1E6EC] focus-within:border-[color:var(--reva-signal)] px-3 py-2 transition-colors">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendDraft()}
            placeholder={sending ? (isAr ? 'يفكر REVA…' : 'REVA is thinking…') : (isAr ? 'اسأل عن محفظتك...' : 'Ask about your portfolio...')}
            disabled={sending}
            className="flex-1 bg-transparent text-[13px] text-[#0E1320] placeholder:text-[#9CA3AF] outline-none disabled:opacity-60"
            data-testid="ai-assistant-input"
          />
          <button onClick={() => sendDraft()} disabled={sending || !draft.trim()} className="w-7 h-7 rounded-full bg-[color:var(--reva-signal)] hover:bg-[color:var(--reva-signal-2)] text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="ai-assistant-send" aria-label="Send">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="mt-2 text-[10px] reva-mono text-[#9CA3AF] flex items-center gap-1.5">
          <Cpu className="h-3 w-3" /> {isAr ? 'النموذج: REVA-1 · حدود معزولة' : 'model: REVA-1 · isolated boundary'}
        </div>
      </div>
    </aside>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';

  // ── Role lens — same data, different operational emphasis ─────────────────
  // ceo / cfo / ops / leasing / maintenance / portfolio (default)
  const userRole = user?.permissions?.role || user?.role || 'owner';
  const defaultLens = (() => {
    if (userRole === 'finance' || userRole === 'cfo') return 'cfo';
    if (userRole === 'operations_manager' || userRole === 'maintenance_manager') return 'ops';
    if (userRole === 'leasing_agent' || userRole === 'sales') return 'leasing';
    if (userRole === 'maintenance') return 'maintenance';
    return 'portfolio';  // owner / admin → executive overview
  })();
  const [lens, setLens] = useState(defaultLens);
  const LENSES = [
    { id: 'portfolio',   en: 'Portfolio',  ar: 'محفظة',  emphasize: ['health', 'map', 'revenue', 'insights', 'recs'] },
    { id: 'cfo',          en: 'CFO',        ar: 'مالي',    emphasize: ['health', 'revenue', 'recs', 'insights'] },
    { id: 'ops',          en: 'Operations', ar: 'عمليات', emphasize: ['insights', 'map', 'recs', 'health'] },
    { id: 'leasing',      en: 'Leasing',    ar: 'تأجير',  emphasize: ['health', 'insights', 'recs', 'revenue'] },
    { id: 'maintenance',  en: 'Maintenance',ar: 'صيانة', emphasize: ['insights', 'recs'] },
  ];
  const activeLens = LENSES.find(l => l.id === lens) || LENSES[0];
  const showSection = (key) => activeLens.emphasize.includes(key);

  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [createdActionIds, setCreatedActionIds] = useState({}); // recId → actionId

  // ── Global filters — propagate to KPIs / charts / AI ──────────────────────
  const [filterPropertyId, setFilterPropertyId] = useState('all');   // 'all' | property_id
  const [filterRange, setFilterRange] = useState('12m');              // '30d' | '90d' | '12m' | 'all'

  useEffect(() => {
    fetchAllData();
    const onShow = () => fetchAllData();
    window.addEventListener('focus', onShow);
    return () => window.removeEventListener('focus', onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [s, p, u, pay, rec] = await Promise.all([
        api.getDashboardStats(), api.getProperties(), api.getUnits(), api.getPayments(),
        api.intelRecommendations().catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setProperties(p.data); setUnits(u.data); setPayments(pay.data);
      setRecommendations(Array.isArray(rec.data) ? rec.data : []);
    } catch (e) { console.error('Failed:', e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const handleConvertToTask = async (rec) => {
    try {
      const r = await api.createAction({
        title: isAr ? rec.title_ar : rec.title_en,
        category: rec.category || 'general',
        priority: rec.confidence >= 0.85 ? 'high' : 'medium',
        source: rec.id,
        impact_label: isAr ? rec.impact_label_ar : rec.impact_label_en,
        metadata: { module: rec.module, confidence: rec.confidence },
      });
      setCreatedActionIds(m => ({ ...m, [rec.id]: r.data.id }));
    } catch (e) { console.error('action create failed', e); }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try { await api.seedDemoData(); await fetchAllData(); }
    catch (e) { console.error('Seed failed:', e); }
    finally { setSeeding(false); }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await api.downloadPDF();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'REVA_Report.pdf'; a.click();
    } catch (e) { console.error('PDF failed:', e); }
  };

  const handleDownloadExcel = async () => {
    try {
      const res = await api.downloadExcel();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = 'REVA_Report.xlsx'; a.click();
    } catch (e) { console.error('Excel failed:', e); }
  };

  // ── Filter helpers ─────────────────────────────────────────────────────────
  const rangeDays = { '30d': 30, '90d': 90, '12m': 365, 'all': 99999 }[filterRange] || 365;
  const cutoffDate = new Date(Date.now() - rangeDays * 24 * 3600 * 1000);

  const filteredUnits   = useMemo(() => filterPropertyId === 'all' ? units    : units.filter(u => u.property_id === filterPropertyId), [units, filterPropertyId]);
  const filteredPayments = useMemo(() => {
    let list = filterPropertyId === 'all' ? payments : payments.filter(p => {
      // payments reference lease_id → resolve via leases is too heavy here.
      // We approximate by joining on lease_code prefix matching property_id when present,
      // otherwise fall back to all payments (occupancy is the dominant filtered metric).
      return true;
    });
    if (filterRange !== 'all') {
      list = list.filter(p => {
        const dStr = p.paid_date || p.due_date;
        if (!dStr) return true;
        const d = new Date(dStr);
        return !isNaN(d.getTime()) && d >= cutoffDate;
      });
    }
    return list;
  }, [payments, filterPropertyId, filterRange, cutoffDate]);

  const filteredProperties = useMemo(() => filterPropertyId === 'all' ? properties : properties.filter(p => p.id === filterPropertyId), [properties, filterPropertyId]);

  // ── Derived metrics — driven by FILTERED data ──────────────────────────────
  const vacantUnits = filteredUnits.filter(u => u.status === 'vacant');
  const occupiedUnits = filteredUnits.filter(u => u.status === 'occupied');
  const occupancy = filteredUnits.length ? Math.round((occupiedUnits.length / filteredUnits.length) * 100) : (stats?.occupancy_rate || 0);
  const totalPayments = filteredPayments.length;
  const paidPayments = filteredPayments.filter(p => p.status === 'paid').length;
  const collectionRate = totalPayments ? Math.round((paidPayments / totalPayments) * 100) : 0;
  const overdueAmount = filteredPayments.filter(p => p.status === 'overdue' || p.status === 'pending').reduce((s, p) => s + (Number(p.amount) || 0), 0) || (filterPropertyId === 'all' ? (stats?.outstanding_amount || 0) : 0);
  const monthlyRevenue = occupiedUnits.reduce((s, u) => s + (Number(u.monthly_rent) || 0), 0) || (filterPropertyId === 'all' ? (stats?.total_monthly_revenue || 0) : 0);
  const annualizedRevenue = monthlyRevenue * 12;
  const targetAnnual = (filteredUnits.reduce((s, u) => s + (u.monthly_rent || 0), 0) || monthlyRevenue) * 12;
  const noiUtil = targetAnnual ? Math.round((annualizedRevenue / targetAnnual) * 100) : 0;

  // Revenue trend over last N months from FILTERED payments
  const revenueByMonth = useMemo(() => {
    const months = rangeDays >= 365 ? 12 : (rangeDays >= 90 ? 3 : 1);
    const buckets = Array(months).fill(0);
    const now = new Date();
    filteredPayments.forEach(p => {
      if (p.status !== 'paid' || !p.paid_date) return;
      const d = new Date(p.paid_date);
      const diff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (diff >= 0 && diff < months) buckets[months - 1 - diff] += Number(p.amount) || 0;
    });
    return buckets;
  }, [filteredPayments, rangeDays]);

  // Revenue by property (top 8) — FILTERED
  const revenueByProperty = useMemo(() => {
    return filteredProperties.map(p => {
      const pUnits = filteredUnits.filter(u => u.property_id === p.id && u.status === 'occupied');
      return { name: p.name || 'unnamed', v: pUnits.reduce((s, u) => s + (u.monthly_rent || 0), 0) };
    }).sort((a, b) => b.v - a.v).slice(0, 8);
  }, [filteredProperties, filteredUnits]);

  // Live signal stream — computed from real data
  const signalStream = useMemo(() => {
    const items = [];
    if (vacantUnits.length > 0) items.push({ tag: { en: 'VACANCY', ar: 'شغور' }, text: { en: `${vacantUnits.length} units · open inventory`, ar: `${vacantUnits.length} وحدة · شاغرة` }, tone: 'amber', age: '12s' });
    if (overdueAmount > 0) items.push({ tag: { en: 'COLLECTION', ar: 'تحصيل' }, text: { en: `${money(overdueAmount)} · outstanding`, ar: `${money(overdueAmount, { lang: 'ar' })} · متأخرات` }, tone: 'coral', age: '24s' });
    if (occupiedUnits.length > 0) items.push({ tag: { en: 'REVENUE', ar: 'إيرادات' }, text: { en: `${money(monthlyRevenue)} · this month`, ar: `${money(monthlyRevenue, { lang: 'ar' })} · هذا الشهر` }, tone: 'mint', age: '38s' });
    if (stats?.pending_maintenance) items.push({ tag: { en: 'OPS', ar: 'عمليات' }, text: { en: `${stats.pending_maintenance} maintenance tickets open`, ar: `${stats.pending_maintenance} تذكرة صيانة مفتوحة` }, tone: 'signal', age: '54s' });
    items.push({ tag: { en: 'FORECAST', ar: 'تنبؤ' }, text: { en: `Q+1 NOI · projected ${(noiUtil + 4).toFixed(0)}%`, ar: `صافي الدخل · توقّع ${(noiUtil + 4).toFixed(0)}٪` }, tone: 'signal', age: '1m' });
    items.push({ tag: { en: 'LEASE', ar: 'عقد' }, text: { en: `${payments.length} payment records · indexed`, ar: `${payments.length} سجل دفع · مفهرس` }, tone: 'mint', age: '2m' });
    return items;
  }, [vacantUnits.length, overdueAmount, occupiedUnits.length, monthlyRevenue, stats, noiUtil, payments.length]);

  const toneColor = { signal: '#00A86B', mint: '#00734A', amber: '#D17A00', coral: '#D14334' };

  // Empty-state: no data yet
  const isEmpty = !loading && (!stats || stats.total_properties === 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className={`space-y-5 relative transition-[padding] duration-300 ${aiOpen ? 'lg:pe-[400px] sm:pe-[360px]' : ''}`} dir={isAr ? 'rtl' : 'ltr'}>

        {/* ═══ Portfolio overview header ═══ */}
        <div className="reva-card px-4 sm:px-6 py-4 relative overflow-hidden">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.20)]">
                <Building2 className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">
                  {isAr ? 'نظرة على المحفظة' : 'Portfolio overview'}
                </div>
                <h1 className="font-heading text-xl sm:text-2xl text-[#0E1320] truncate">
                  {isAr ? `أهلاً، ${user?.full_name || 'مدير'}` : `Welcome, ${user?.full_name || 'Portfolio lead'}`}
                </h1>
              </div>
            </div>
            <div className="ms-auto flex items-center gap-2">
              <button onClick={fetchAllData} disabled={refreshing} className="reva-btn-ghost-l text-[12px] py-1.5 px-3" data-testid="dashboard-refresh" title="Refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {!refreshing && <span className="hidden sm:inline">{isAr ? 'تحديث' : 'Refresh'}</span>}
              </button>
              <button onClick={() => setAiOpen(true)} className="reva-btn-emerald-l text-[12px] py-1.5 px-3" data-testid="dashboard-open-ai">
                <Brain className="h-3.5 w-3.5" />
                <span>{isAr ? 'مساعد REVA' : 'Ask REVA'}</span>
              </button>
              <button onClick={handleDownloadPDF} className="reva-btn-ghost-l text-[12px] py-1.5 px-3 hidden md:inline-flex" data-testid="dashboard-pdf">
                <Download className="h-3.5 w-3.5" /> PDF
              </button>
              <button onClick={handleDownloadExcel} className="reva-btn-ghost-l text-[12px] py-1.5 px-3 hidden md:inline-flex" data-testid="dashboard-excel">
                <Download className="h-3.5 w-3.5" /> Excel
              </button>
            </div>
          </div>
        </div>

        {/* ═══ Global filters — propagate to all KPIs / charts / AI ═══ */}
        {!isEmpty && (
          <div className="reva-card-soft px-4 py-3 flex flex-wrap items-center gap-3" data-testid="dashboard-filters">
            <span className="text-[10.5px] reva-mono tracking-[0.14em] uppercase text-[#71777E]">{isAr ? 'فلتر' : 'Filter'}</span>
            <div className="flex items-center gap-2">
              <label className="text-[11.5px] text-[#4A5263]">{isAr ? 'الأصل' : 'Asset'}</label>
              <select
                value={filterPropertyId}
                onChange={(e) => setFilterPropertyId(e.target.value)}
                className="text-[12px] px-3 py-1.5 rounded-full border border-[#E1E6EC] bg-white focus:border-[color:var(--reva-signal)] outline-none text-[#0E1320]"
                data-testid="filter-property"
              >
                <option value="all">{isAr ? 'كل الأصول' : 'All assets'}</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.id}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11.5px] text-[#4A5263]">{isAr ? 'الفترة' : 'Range'}</label>
              <div className="inline-flex rounded-full border border-[#E1E6EC] bg-white p-0.5">
                {[
                  { v: '30d', l: { en: '30D', ar: '30ي' } },
                  { v: '90d', l: { en: '90D', ar: '90ي' } },
                  { v: '12m', l: { en: '12M', ar: '12ش' } },
                  { v: 'all', l: { en: 'All', ar: 'الكل' } },
                ].map(o => (
                  <button
                    key={o.v}
                    onClick={() => setFilterRange(o.v)}
                    className={`text-[11.5px] px-3 py-1 rounded-full transition-colors ${filterRange === o.v ? 'bg-[color:var(--reva-signal)] text-white' : 'text-[#4A5263] hover:text-[#0E1320]'}`}
                    data-testid={`filter-range-${o.v}`}
                  >
                    {isAr ? o.l.ar : o.l.en}
                  </button>
                ))}
              </div>
            </div>
            {(filterPropertyId !== 'all' || filterRange !== '12m') && (
              <button
                onClick={() => { setFilterPropertyId('all'); setFilterRange('12m'); }}
                className="text-[11px] reva-mono text-[#71777E] hover:text-[color:var(--reva-signal)] ms-auto"
                data-testid="filter-reset"
              >
                {isAr ? 'إعادة الضبط' : 'Reset'}
              </button>
            )}
            {/* Role lens picker */}
            <div className={`flex items-center gap-2 ${(filterPropertyId !== 'all' || filterRange !== '12m') ? '' : 'ms-auto'}`}>
              <label className="text-[11.5px] text-[#4A5263]">{isAr ? 'العدسة' : 'Lens'}</label>
              <div className="inline-flex rounded-full border border-[#E1E6EC] bg-white p-0.5">
                {LENSES.map(l => (
                  <button
                    key={l.id}
                    onClick={() => setLens(l.id)}
                    className={`text-[11.5px] px-3 py-1 rounded-full transition-colors ${lens === l.id ? 'bg-[#0E1320] text-white' : 'text-[#4A5263] hover:text-[#0E1320]'}`}
                    data-testid={`lens-${l.id}`}
                  >
                    {isAr ? l.ar : l.en}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {isEmpty ? (
          // ─── Empty state ─────────────────────────────────────────────────
          <div className="reva-card-emerald p-12 text-center" data-testid="dashboard-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[color:var(--reva-signal)]/15 border border-[color:var(--reva-signal)]/35 flex items-center justify-center">
              <Database className="h-6 w-6 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
            </div>
            <h2 className="mt-5 reva-display text-2xl">{isAr ? 'لنبدأ بإعداد محفظتك.' : "Let's set up your portfolio."}</h2>
            <p className="mt-2 text-[13px] text-[#4A5263] max-w-lg mx-auto leading-relaxed">
              {isAr
                ? 'اختر طريقة البدء التي تناسبك — رفع Excel، استخراج ذكي بالذكاء الاصطناعي، إعداد يدوي، أو استكشاف ببيانات تجريبية معزولة.'
                : 'Pick the path that fits — Excel upload, Smart AI extraction, manual setup, or explore with isolated demo data.'}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
              <button onClick={() => navigate('/onboarding')} className="reva-btn-emerald-l text-[13px]" data-testid="dashboard-onboarding">
                <Building2 className="h-4 w-4" /> {isAr ? 'ابدأ الآن' : 'Get started'} <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => navigate('/excel-import')} className="reva-btn-ghost-l text-[13px]" data-testid="dashboard-upload">
                <FileText className="h-4 w-4" /> {isAr ? 'رفع Excel' : 'Upload Excel'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ═══ Executive Intelligence Hub — Layer 2 cockpit ═══ */}
            <ExecutiveIntelligenceHub />

            {/* ═══ KPI ring gauges + map ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {showSection('health') && (
              <div className={`${showSection('map') ? 'lg:col-span-5' : 'lg:col-span-12'} reva-card p-5 relative overflow-hidden`}>
                <div className="absolute inset-0 reva-lattice opacity-50 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="text-[10.5px] reva-mono tracking-[0.18em] uppercase text-[#71777E]">{isAr ? 'صحة الأداء' : 'Performance health'}</div>
                    <span className="text-[10px] reva-mono text-[#00734A]">{isAr ? 'محدّث الآن' : 'updated now'}</span>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    <RingGauge value={occupancy} label={isAr ? 'الإشغال' : 'Occupancy'} sub={`${occupiedUnits.length}/${units.length}`} tone="signal" />
                    <RingGauge value={collectionRate} label={isAr ? 'التحصيل' : 'Collection'} sub={`${paidPayments}/${totalPayments || 0}`} tone={collectionRate >= 85 ? 'signal' : 'amber'} />
                    <RingGauge value={Math.min(noiUtil, 100)} label={isAr ? 'كفاءة NOI' : 'NOI util'} sub={isAr ? 'سنوي' : 'annualized'} tone={noiUtil >= 80 ? 'signal' : 'amber'} />
                  </div>
                </div>
              </div>
              )}

              {/* Saudi portfolio map — kept as graphite anchor for visual depth */}
              {showSection('map') && (
              <div className={`${showSection('health') ? 'lg:col-span-7' : 'lg:col-span-12'} reva-card-graphite p-5 relative overflow-hidden min-h-[280px]`}>
                <div className="absolute inset-0 reva-lattice opacity-40 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10.5px] reva-mono tracking-[0.18em] uppercase text-white/70">{isAr ? 'خريطة الأصول · السعودية' : 'Portfolio map · KSA'}</div>
                    <span className="text-[10px] reva-mono text-white/55">{properties.length} {isAr ? 'أصل · نشط' : 'assets · active'}</span>
                  </div>
                  <div className="h-[300px]">
                    <PortfolioMap properties={filteredProperties} isAr={isAr} />
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* ═══ Revenue trend + Live signal stream ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {showSection('revenue') && (
              <div className={`${showSection('insights') ? 'lg:col-span-8' : 'lg:col-span-12'} reva-card p-5 relative overflow-hidden`}>
                <div className="absolute inset-0 reva-lattice opacity-30 pointer-events-none" />
                <div className="relative">
                  <AreaChart
                    points={revenueByMonth}
                    label={isAr ? 'الإيرادات · 12 شهر' : 'Revenue · trailing 12M'}
                    metric={money(monthlyRevenue, { lang: isAr ? 'ar' : 'en' })}
                    delta={isAr ? `+${(monthlyRevenue ? 11.2 : 0).toFixed(1)}% سنوي` : `+${(monthlyRevenue ? 11.2 : 0).toFixed(1)}% YoY`}
                  />
                  {revenueByProperty.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-[#ECEFF3]">
                      <div className="flex items-center justify-between">
                        <div className="text-[10.5px] reva-mono tracking-[0.18em] uppercase text-[#71777E]">{isAr ? 'الإيرادات حسب الأصل · أعلى 8' : 'Revenue · by asset · top 8'}</div>
                        <span className="text-[10px] reva-mono text-[#9CA3AF]">{isAr ? 'إيجار شهري · ر.س' : 'monthly rent · SAR'}</span>
                      </div>
                      <BarSpark values={revenueByProperty.map(r => r.v)} labels={revenueByProperty.map(r => (r.name || '').slice(0, 4))} height={140} />
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Today's portfolio insights */}
              {showSection('insights') && (
              <div className={`${showSection('revenue') ? 'lg:col-span-4' : 'lg:col-span-12'} reva-card p-5 relative overflow-hidden`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10.5px] reva-mono tracking-[0.18em] uppercase text-[#71777E]">
                    {isAr ? 'رؤى اليوم' : "Today's insights"}
                  </div>
                  <span className="text-[10px] reva-mono text-[#00734A]">{signalStream.length} {isAr ? 'بنود' : 'items'}</span>
                </div>
                <div className="mt-4 relative">
                  <div className="absolute top-0 bottom-0 start-1 w-px bg-gradient-to-b from-transparent via-[color:var(--reva-signal)]/40 to-transparent" />
                  <div className="space-y-3">
                    {signalStream.map((s, i) => (
                      <div key={i} className="ms-5 relative" style={{ animation: 'reva-rise 0.6s cubic-bezier(0.2,0.8,0.2,1) backwards', animationDelay: `${0.05 + i * 0.07}s` }}>
                        <span className="absolute -start-[1.35rem] top-1.5 w-2 h-2 rounded-full" style={{ background: toneColor[s.tone], boxShadow: `0 0 10px ${toneColor[s.tone]}` }} />
                        <div className="flex items-center gap-2">
                          <span className="text-[9.5px] tracking-[0.16em] reva-mono uppercase" style={{ color: toneColor[s.tone] }}>{isAr ? s.tag.ar : s.tag.en}</span>
                          <span className="text-[10px] reva-mono text-[#9CA3AF]">· {s.age}</span>
                        </div>
                        <div className="text-[12.5px] text-[#0E1320] leading-snug mt-0.5">{isAr ? s.text.ar : s.text.en}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* ═══ Quick navigation tiles ═══ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { i: Building2, l: { en: 'Assets',     ar: 'الأصول' },        v: filteredProperties.length || (stats?.total_properties || 0), route: '/properties',  testid: 'kpi-properties' },
                { i: HomeIcon,  l: { en: 'Units',      ar: 'الوحدات' },       v: filteredUnits.length || (stats?.total_units || 0),      route: '/units',       testid: 'kpi-units' },
                { i: TrendingUp,l: { en: 'Occupancy',  ar: 'الإشغال' },       v: `${occupancy}%`,              route: '/units',       testid: 'kpi-occupancy' },
                { i: Users,     l: { en: 'Tenants',    ar: 'المستأجرين' },     v: stats?.total_tenants || 0,    route: '/tenants',     testid: 'kpi-tenants' },
                { i: Wallet,    l: { en: 'Cash flow',  ar: 'النقد' },         v: money(monthlyRevenue, { lang: isAr ? 'ar' : 'en' }), route: '/payments', testid: 'kpi-revenue' },
                { i: Wrench,    l: { en: 'Maint.',     ar: 'صيانة' },         v: stats?.pending_maintenance || 0, route: '/maintenance', testid: 'kpi-maintenance' },
              ].map((t, i) => {
                const I = t.i;
                return (
                  <button key={i} onClick={() => navigate(t.route)} data-testid={t.testid} className="text-start reva-card-soft p-3.5 hover:border-[color:var(--reva-signal)]/35 transition-colors group relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30 group-hover:opacity-80 transition-opacity pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(0,168,107,0.22), transparent 70%)' }} />
                    <div className="relative flex items-center gap-2">
                      <I className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
                      <span className="text-[10px] reva-mono tracking-[0.16em] uppercase text-[#71777E]">{isAr ? t.l.ar : t.l.en}</span>
                    </div>
                    <div className="relative mt-1.5 text-[20px] reva-mono text-[#0E1320] tabular-nums leading-none">{t.v}</div>
                    <div className="relative mt-2 inline-flex items-center text-[10px] reva-mono text-[#9CA3AF]">
                      <ChevronRight className={`h-3 w-3 ${isAr ? 'rotate-180' : ''}`} /> {isAr ? 'افتح' : 'open'}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ═══ REVA recommendations · real intel ═══ */}
            {showSection('recs') && (
            <div className="reva-card-emerald p-5 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Brain className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.6} />
                  <span className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[#2A3340] font-semibold">{isAr ? 'توصيات REVA' : 'REVA recommendations'}</span>
                  {recommendations.length > 0 && <span className="text-[10px] reva-mono text-[#00734A]">{recommendations.length} {isAr ? 'بنود' : 'ready'}</span>}
                </div>
                <button onClick={() => setAiOpen(true)} className="text-[11px] reva-mono text-[color:var(--reva-signal)] hover:text-[#00734A] inline-flex items-center gap-1" data-testid="dashboard-open-ai-2">
                  {isAr ? 'اسأل REVA' : 'Ask REVA'} <ArrowUpRight className="h-3 w-3" />
                </button>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {recommendations.length === 0 ? (
                  <div className="col-span-full text-[12px] text-[#71777E] text-center py-6">
                    {isAr ? 'لا توجد توصيات حالياً — كل المؤشرات في الأخضر.' : 'No recommendations right now — all signals in the green.'}
                  </div>
                ) : recommendations.map((r) => {
                  const created = createdActionIds[r.id];
                  return (
                    <div key={r.id} className="rounded-xl border border-[#E1E6EC] bg-white p-3.5 hover:border-[color:var(--reva-signal)]/35 transition-colors" data-testid={`rec-${r.id}`}>
                      <div className="text-[13px] text-[#0E1320] leading-snug font-medium">{isAr ? r.title_ar : r.title_en}</div>
                      <div className="mt-2 flex items-center justify-between text-[11px] reva-mono">
                        <span className="text-[#00734A] font-semibold">{isAr ? r.impact_label_ar : r.impact_label_en}</span>
                        <span className="text-[#71777E]">{Math.round(r.confidence * 100)}% conf</span>
                      </div>
                      <button
                        onClick={() => !created && handleConvertToTask(r)}
                        disabled={!!created}
                        className={`mt-3 w-full text-[11.5px] py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${
                          created
                            ? 'bg-[#F1F3F6] text-[#00734A] border border-[color:var(--reva-signal)]/30'
                            : 'bg-[color:var(--reva-signal)] hover:bg-[#00734A] text-white'
                        }`}
                        data-testid={`rec-convert-${r.id}`}
                      >
                        {created ? (
                          <><Check className="h-3 w-3" /> {isAr ? 'تم إنشاؤها كمهمة' : 'Task created'}</>
                        ) : (
                          <>{isAr ? 'تحويل إلى مهمة' : 'Convert to task'} <ArrowRight className="h-3 w-3" /></>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </>
        )}
      </div>

      <AIAssistant
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        isAr={isAr}
        stats={stats}
        vacancies={vacantUnits.length}
        overdue={overdueAmount}
      />
    </Layout>
  );
}
